import React, { useMemo, useState, useEffect } from 'react';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Anchor,
  Center,
  Group,
  Stack,
  Switch,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../SQLEditorPopup';

const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder style={{ width: 400 }}>
    <Text size="sm" fw={500} mb={4}>
      Legend
    </Text>
    <Stack gap={4}>
      {[
        { color: 'var(--mantine-color-green-6)', label: 'Margin ≥ 20%' },
        { color: 'var(--mantine-color-yellow-6)', label: 'Margin between 15% and 20%' },
        { color: 'var(--mantine-color-red-6)', label: 'Margin < 15%' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

function PurchasingIndividualInventoryPlannedSpendDetails() {
  const location = useLocation();
  const { ccode } = useParams();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });

  const [activeTab, setActiveTab] = useState(period);
  
  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Clear handlers for proper dialog closing
  const handleCloseMembership = () => setOpenMembership(false);
  const handleCloseSummaryQuery = () => setOpenSummaryQuery(false);
  const handleCloseDetailsQuery = () => setOpenDetailsQuery(false);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  // Build URL with period, ccode, and filter parameters
  const url = `${config.apiBaseUrl}/supply-chain/purchasing/individual/scorecard/inventory-planned-spend-details?ccode=${ccode}&period=${period}&filter=${warehouseFilter}`;

  const { data, isLoading, error } = useAuthenticatedData(url);
  const { data: purchaserName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  // Handle tab changes for period selection
  const handleTabChange = (newValue) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('period', newValue);
    newSearchParams.set('filter', warehouseFilter);
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
    setActiveTab(newValue);
  };

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index,
      // Extract values from the nested structure and map to expected field names
      entered: item.values?.entered,
      partnumber: item.values?.partnumber,
      condition: item.values?.condition,
      accountName: item.values?.accountname,
      fullname: item.values?.fullname,
      qtyOrdered: item.values?.qtyordered,
      unitPrice: item.values?.unitprice,
      unitSellPrice: item.values?.unitsellprice,
      spend: item.values?.spend,
      projectedRev: item.values?.projectedrev,
      projectedMargin: item.values?.projectedmargin,
      marginPercent: item.values?.marginpercent,
      shipDate: item.values?.shipdate,
      dueDate: item.values?.duedate,
      docNo: item.values?.docno,
      userLine: item.values?.userline,
      docType: item.values?.doctype,
    }));
  }, [data]);

  const generateExportFileName = () => `Purchasing-${purchaserName}-${period}-inventory-planned-spend-details`;

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Entered': row.entered ? new Date(row.entered).toLocaleDateString() : '',
      'Part Number': row.partnumber || '',
      'Condition': row.condition || '',
      'Vendor': row.accountName || '',
      'Buyer': row.fullname || '',
      'Qty': row.qtyOrdered || '',
      'Unit Price': row.unitPrice || '',
      'Unit Sell Price': row.unitSellPrice || '',
      'Spend': row.spend || '',
      'Projected Revenue': row.projectedRev || '',
      'Projected Margin': row.projectedMargin || '',
      'Margin %': row.marginPercent ? `${Number(row.marginPercent).toFixed(2)}%` : '',
      'Ship Date': row.shipDate ? new Date(row.shipDate).toLocaleDateString() : '',
      'Due Date': row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '',
      'Pentagon Doc#': row.docNo || '',
      'Line': row.userLine || '',
      'Doc Type': row.docType || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'entered',
        header: 'Entered',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      { accessorKey: 'condition', header: 'Condition', size: 100 },
      { accessorKey: 'accountName', header: 'Vendor', size: 150 },
      { accessorKey: 'fullname', header: 'Buyer', size: 150 },
      { accessorKey: 'qtyOrdered', header: 'Qty', size: 80 },
      {
        accessorKey: 'unitPrice',
        header: 'Unit Price',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'unitSellPrice',
        header: 'Unit Sell Price',
        size: 140,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'spend',
        header: 'Spend',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedRev',
        header: 'Projected Revenue',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedMargin',
        header: 'Projected Margin',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'marginPercent',
        header: 'Margin %',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
      {
        accessorKey: 'shipDate',
        header: 'Ship Date',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'docNo', header: 'Pentagon Doc#', size: 120 },
      { accessorKey: 'userLine', header: 'Line', size: 80 },
      { accessorKey: 'docType', header: 'Doc Type', size: 140 },
    ],
    []
  );

  const getRowStyle = (row) => {
    const margin = Number(row.original.marginPercent);
    if (isNaN(margin)) return {};
    
    if (margin >= 20) {
      return {
        backgroundColor: 'var(--mantine-color-green-1)',
        color: 'var(--mantine-color-green-9)',
      };
    }
    if (margin >= 15 && margin < 20) {
      return {
        backgroundColor: 'var(--mantine-color-yellow-1)',
        color: 'var(--mantine-color-yellow-9)',
      };
    }
    if (margin < 15) {
      return {
        backgroundColor: 'var(--mantine-color-red-1)',
        color: 'var(--mantine-color-red-9)',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  if (isLoading || isLoadingName) return <Center><Loader /></Center>;
  if (error) return <Alert color="red" title="Error">{error.message}</Alert>;
  if (errorName) return <Alert color="red" title="Error">{errorName.message}</Alert>;

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header Section with Title and Switch */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Individual Scorecard - Inventory Planned Spend Details - {purchaserName}
        </Text>

        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* Technical options: small links placed under the title */}
      <Group gap="sm" mb="sm">
 
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              `/query-commander/editor?queryKey=purchasing_individual_planned_inventory_spend_summary&ccode=${ccode}`,
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              `/query-commander/editor?queryKey=purchasing_individual_planned_inventory_spend_details&ccode=${ccode}`,
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="flex-start" gap="sm">
        <Card shadow="sm" padding="xs" radius="sm" withBorder style={{ flex: 1, minWidth: 300 }}>
          <Text size="sm" fw={500} mb={4}>
            About the Data
          </Text>
          <Text size="xs" c="dimmed">
            These are purchase orders for parts to stock entered by {purchaserName}. The price of the part as it existed in Pentagon at the time of the PO is used here - it doesn't matter what the sell price happens to be in Pentagon right now. The projected revenue is calculated by multiplying the quantity ordered by the projected sell price.
            Currently you're seeing data {period}.
          </Text>
        </Card>
        <ColorLegend />
      </Group>

      {/* Tabs for period selection */}
      <Tabs value={period.toLowerCase()} onChange={handleTabChange} mb="sm">
        <Tabs.List>
          <Tabs.Tab value="mtd">Month-to-Date</Tabs.Tab>
          <Tabs.Tab value="qtd">Quarter-to-Date</Tabs.Tab>
          <Tabs.Tab value="ytd">Year-to-Date</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Paper shadow="sm" p="sm" style={{ height: 600 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>

      
    </Paper>
  );
}

export default PurchasingIndividualInventoryPlannedSpendDetails;